import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Tooltip
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import apiService from "../services/api";
// Removed arrow icons in favor of text buttons
import StopIcon from "@mui/icons-material/Stop";

// Status chip color mapping
const getLifecycleStateColor = (state) => {
  switch (state.toLowerCase()) {
    case "inservice":
      return "success";
    case "pending":
      return "warning";
    case "terminating":
      return "error";
    case "standby":
      return "info";
    default:
      return "default";
  }
};

const getHealthStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case "healthy":
      return "success";
    case "unhealthy":
      return "error";
    default:
      return "default";
  }
};

// Sticky columns styling for headers
const HeaderNumberCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 2,
  width: '40px',
  padding: '8px',
  textAlign: 'center'
}));

// Sticky columns styling for data cells
const DataNumberCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  backgroundColor: 'inherit',
  zIndex: 1,
  width: '40px',
  minWidth: '40px',
  padding: '8px',
  textAlign: 'center'
}));

const InstancesTable = React.forwardRef(({ instances, loading, error, desiredCount = 2 }, ref) => {
  const theme = useTheme();
  const [asgSize, setAsgSize] = useState(null);
  const [asgSizeLoading, setAsgSizeLoading] = useState(false);
  const [asgSizeError, setAsgSizeError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scaleAction, setScaleAction] = useState(null);
  const [scaling, setScaling] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [instanceToTerminate, setInstanceToTerminate] = useState(null);
  const [terminating, setTerminating] = useState(false);

  // Expose fetchAsgSize to parent component via ref
  React.useImperativeHandle(ref, () => ({
    refreshAsgSize: fetchAsgSize
  }));

  const fetchAsgSize = async () => {
    setAsgSizeLoading(true);
    try {
      const data = await apiService.getASGSize();
      setAsgSize(data.size);
      setAsgSizeError(null);
    } catch (error) {
      console.error('Error fetching ASG size:', error);
      setAsgSizeError('Failed to load ASG size');
    } finally {
      setAsgSizeLoading(false);
    }
  };

  const handleScaleClick = (action) => {
    setScaleAction(action);
    setDialogOpen(true);
  };

  const handleScaleConfirm = async () => {
    setDialogOpen(false);
    setScaling(true);
    try {
      await apiService.scaleASG(scaleAction);
      // Refresh ASG size after scaling
      await fetchAsgSize();
    } catch (error) {
      console.error(`Error scaling ASG ${scaleAction}:`, error);
      setAsgSizeError(`Failed to scale ASG ${scaleAction}`);
    } finally {
      setScaling(false);
    }
  };

  const handleTerminateClick = (instanceId) => {
    setInstanceToTerminate(instanceId);
    setTerminateDialogOpen(true);
  };

  const handleTerminateConfirm = async () => {
    setTerminateDialogOpen(false);
    setTerminating(true);

    try {
      await apiService.terminateInstance(instanceToTerminate);
      // Refresh ASG size after termination
      await fetchAsgSize();
      // We don't refresh instances here as the parent component will handle that
    } catch (error) {
      console.error(`Error terminating instance ${instanceToTerminate}:`, error);
      setAsgSizeError(`Failed to terminate instance ${instanceToTerminate}`);
    } finally {
      setTerminating(false);
      setInstanceToTerminate(null);
    }
  };

  const handleTerminateCancel = () => {
    setTerminateDialogOpen(false);
    setInstanceToTerminate(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    fetchAsgSize();
  }, []);

  // Create a fixed array of 2 rows
  const createFixedRows = () => {
    // If we have instances, use them for the first rows
    const rows = [];

    // Add actual instances
    if (instances && instances.length > 0) {
      for (let i = 0; i < Math.min(instances.length, 2); i++) {
        rows.push({
          rowNum: i + 1,
          ...instances[i],
          isActive: true
        });
      }
    }

    // Fill remaining rows with empty placeholders
    while (rows.length < 2) {
      rows.push({
        rowNum: rows.length + 1,
        dns_name: '-',
        instance_id: '-',
        instance_type: '-',
        lifecycle_state: '-',
        health_status: '-',
        public_ip: '-',
        isActive: false
      });
    }

    return rows;
  };

  // Show loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">
          Error loading instances: {error.message || "Unknown error"}
        </Typography>
      </Box>
    );
  }

  const fixedRows = createFixedRows();
  // We should show dashes when desired count is 0 or 1, but only for rows that aren't active instances
  // This ensures that actual running instances always show their data

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mt: 2, mb: 2, overflow: 'auto' }}>
        <Table aria-label="COMSOL AWS Server instances table" sx={{ tableLayout: 'auto', minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <HeaderNumberCell sx={{ width: '40px', minWidth: '40px' }}>#</HeaderNumberCell>
              <TableCell sx={{ width: '22%', minWidth: '150px' }}>DNS Name</TableCell>
              <TableCell sx={{ width: '18%', minWidth: '130px' }}>Instance ID</TableCell>
              <TableCell sx={{ width: '12%', minWidth: '100px' }}>Instance Type</TableCell>
              <TableCell sx={{ width: '12%', minWidth: '100px' }}>Lifecycle State</TableCell>
              <TableCell sx={{ width: '12%', minWidth: '100px' }}>Health Status</TableCell>
              <TableCell sx={{ width: '15%', minWidth: '100px' }}>Public IP</TableCell>
              <TableCell align="center" sx={{ width: '60px', minWidth: '60px' }}>Stop</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fixedRows.map((row) => {
              // Show dashes when:
              // 1. The row is not an active instance OR
              // 2. The desired count is 0 or 1 (regardless of whether the instance is active)
              const showDashes = !row.isActive || desiredCount <= 1;

              return (
                <TableRow
                  key={row.rowNum}
                  sx={{
                    opacity: !row.isActive ? 0.5 : 1,
                    backgroundColor: !row.isActive ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)') : 'inherit'
                  }}
                >
                  <DataNumberCell>{row.rowNum}</DataNumberCell>
                  <TableCell>{showDashes ? "-" : row.dns_name}</TableCell>
                  <TableCell component="th" scope="row">
                    {showDashes ? "-" : row.instance_id}
                  </TableCell>
                  <TableCell>{showDashes ? "-" : row.instance_type}</TableCell>
                  <TableCell>
                    {showDashes ? (
                      "-"
                    ) : (
                      <Chip
                        label={row.lifecycle_state}
                        color={getLifecycleStateColor(row.lifecycle_state)}
                        size="small"
                        onClick={() => {}}
                        variant="filled"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {showDashes ? (
                      "-"
                    ) : (
                      <Chip
                        label={row.health_status}
                        color={getHealthStatusColor(row.health_status)}
                        size="small"
                        onClick={() => {}}
                        variant="filled"
                      />
                    )}
                  </TableCell>
                  <TableCell>{showDashes ? "-" : row.public_ip}</TableCell>
                  <TableCell align="center">
                    {row.isActive && row.lifecycle_state === 'InService' && row.health_status === 'Healthy' && row.dns_name !== '-' ? (
                      <Tooltip title="Terminate instance">
                        <IconButton
                          size="small"
                          onClick={() => handleTerminateClick(row.instance_id)}
                          disabled={terminating}
                          sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)' }}
                        >
                          <StopIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ASG Size Label */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        {asgSizeLoading ? (
          <Box display="flex" alignItems="center">
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">Loading ASG size...</Typography>
          </Box>
        ) : asgSizeError ? (
          <Typography variant="body2" color="error">{asgSizeError}</Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              <strong>Current Autoscaling Group Size:</strong> {asgSize !== null ? asgSize : '-'} out of 2
            </Typography>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={() => handleScaleClick('up')}
              disabled={scaling || asgSizeLoading || asgSize >= 2}
              sx={{ mr: 1, minWidth: '90px' }}
            >
              Start New Instance
            </Button>
            {scaling && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Scaling Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to scale the autoscaling group {scaleAction === 'up' ? 'up' : 'down'}?
            {scaleAction === 'down' && asgSize <= 1 && (
              <Typography color="error" sx={{ mt: 1 }}>
                Warning: Scaling down will reduce the ASG to 0 instances, which may affect service availability.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleScaleConfirm} color="primary" variant="contained" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terminate Instance Confirmation Dialog */}
      <Dialog
        open={terminateDialogOpen}
        onClose={handleTerminateCancel}
        aria-labelledby="terminate-dialog-title"
        aria-describedby="terminate-dialog-description"
      >
        <DialogTitle id="terminate-dialog-title">
          Terminate Instance Confirmation
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="terminate-dialog-description">
            Are you sure you want to terminate instance {instanceToTerminate}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTerminateCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleTerminateConfirm} color="error" autoFocus>
            Terminate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default InstancesTable;
